import React, { forwardRef, isValidElement } from 'react'

import cn from 'classnames'

import classes from './text-input.module.scss'
import { TextInputProps } from './text-input.types'
import ErrorList from '../error-list'
import { Input } from '../input/input'
import { Label } from '../label/label'
import { SupportingText } from '../supporting-text/supporting-text'

/***
 * Design system: https://www.figma.com/file/RE9n7PIwHG7A7WHA7kEVhN/WEB-Components?node-id=3214%3A423
 */

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      labelContent,
      fieldDescription,
      type = 'text',
      error = null,
      onReset,
      errorDataTestId,
      descriptionDataTestId,
      dataTestId,
      wrapperClassName,
      descriptionClassName,
      alwaysShowReset = false,
      showResetButton = true,
      isLegacyStyle = false,
      ...props
    },
    ref
  ) => {
    const descriptionContent = Array.isArray(fieldDescription)
      ? fieldDescription.map((line, index) => (
          <React.Fragment key={`${line}-${index}`}>
            {line}
            {index < fieldDescription.length - 1 && <br />}
          </React.Fragment>
        ))
      : fieldDescription

    return (
      <div
        className={cn(
          classes.inputWrapper,
          {
            [classes.inputWrapperLegacy]: isLegacyStyle,
          },
          wrapperClassName
        )}
      >
        <Label
          htmlFor={props.name || ''}
          text={labelContent}
          isLegacyStyle={isLegacyStyle}
        >
          <Input
            type={type}
            id={props.name}
            error={!!error}
            onReset={onReset}
            alwaysShowReset={alwaysShowReset}
            showResetButton={showResetButton}
            dataTestId={dataTestId}
            isLegacyStyle={isLegacyStyle}
            {...props}
            ref={ref}
          />
          {error && !isLegacyStyle && (
            <SupportingText
              isError
              dataTestId={errorDataTestId || props.name + '-error-tooltip'}
            >
              {error}
            </SupportingText>
          )}
          {error && isLegacyStyle && (
            <ErrorList
              inputId={props.name}
              submitFailed={!!error}
              touched={!!error}
              errors={[error]}
              dataTestId={`${dataTestId}-error`}
            />
          )}
          {fieldDescription && !error && isValidElement(fieldDescription) ? (
            fieldDescription
          ) : (
            <SupportingText
              className={descriptionClassName}
              dataTestId={
                descriptionDataTestId || props.name + '-description-tooltip'
              }
            >
              {descriptionContent}
            </SupportingText>
          )}
        </Label>
      </div>
    )
  }
)
